import React, { useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Phone UI primitives, ported from the Flutter app's Stitch design
 * (UTMS-MOBILE/utms_mobile/lib/main.dart): frosted-glass round buttons, the
 * drone chip, the telemetry card and bottom sheets. Styles: styles/MobileView.css.
 */

export function Icon({ name, size = 22, fill = false, style }) {
    return (
        <span
            className="material-symbols-outlined"
            style={{ fontSize: size, fontVariationSettings: fill ? "'FILL' 1" : undefined, ...style }}
        >{name}</span>
    );
}

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

export const batteryColor = (pct) => (pct == null ? 'var(--m-on-surface-variant)'
    : pct >= 50 ? 'var(--m-green)' : pct >= 20 ? 'var(--m-amber)' : 'var(--m-error)');

const fmtCoord = (v, pos, neg) => (v == null ? '—' : `${Math.abs(v).toFixed(4)}° ${v >= 0 ? pos : neg}`);

/** Glass round icon button (Flutter _Glass + _CircleButton); with `label` it becomes a pill. */
export function RoundButton({ icon, onClick, title, size = 48, neon = false, active = false, color, label }) {
    return (
        <button
            type="button"
            className={`m-b m-glass m-round${label ? ' m-pill' : ''}${neon ? ' neon' : ''}${active ? ' active' : ''}`}
            style={{ width: label ? undefined : size, height: size, color }}
            onClick={onClick}
            title={title}
            aria-label={title}
        >
            <Icon name={icon} size={22} />
            {label}
        </button>
    );
}

/** Bottom sheet — the phone form of the Flutter dialogs. Portaled so a glass
 *  ancestor (backdrop-filter makes a containing block) can't trap it. */
export function Sheet({ title, onClose, children, actions = null }) {
    return createPortal(
        <div className="m-sheet-backdrop" onClick={onClose}>
            <div className="m-sheet" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
                <div className="m-sheet-grip" />
                {title && (
                    <div className="m-sheet-head">
                        <span className="m-sheet-title">{title}</span>
                        {actions}
                        <button type="button" className="m-b m-icon-btn" onClick={onClose} aria-label="Close">
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                )}
                <div className="m-sheet-body">{children}</div>
            </div>
        </div>,
        document.body,
    );
}

/** Right-edge drawer for full-height panels (fleet manager). */
export function Drawer({ onClose, children }) {
    return createPortal(
        <div className="m-sheet-backdrop" onClick={onClose}>
            <aside className="m-drawer" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="m-b m-icon-btn m-drawer-close" onClick={onClose} aria-label="Close">
                    <Icon name="close" size={20} />
                </button>
                {children}
            </aside>
        </div>,
        document.body,
    );
}

/** Chevron button + active drone name, battery and position (Flutter map tab, top-left). */
export function DroneChip({ drone, telemetry, onOpen }) {
    const tel = telemetry || {};
    const battery = num(tel.battery_remaining);
    const live = !!tel.connected;
    return (
        <div className="m-drone-chip">
            <button type="button" className="m-b m-glass neon m-chevron" onClick={onOpen} aria-label="Select drone">
                <Icon name="chevron_right" size={26} />
            </button>
            <button type="button" className="m-b m-drone-chip-text" onClick={onOpen}>
                <span className="m-drone-chip-name">
                    {drone ? (drone.name || drone.id).toUpperCase() : 'NO DRONE'}
                    {drone && (
                        <span style={{ color: live ? batteryColor(battery) : 'var(--m-error)' }}>
                            {live ? (battery != null ? `${Math.round(battery)}%` : '—') : 'OFFLINE'}
                        </span>
                    )}
                </span>
                <span className="m-drone-chip-sub">
                    {live && num(tel.lat) ? `${fmtCoord(num(tel.lat), 'N', 'S')}, ${fmtCoord(num(tel.lon), 'E', 'W')}` : 'TAP TO SELECT'}
                </span>
            </button>
        </div>
    );
}

/** "Active drones" picker — connected first, offline dimmed. */
export function DroneSheet({ drones = [], dronesTelemetry = {}, selectedDroneId, onSelect, onClose, title = 'ACTIVE DRONES' }) {
    const rows = drones
        .map((d) => ({ d, tel: dronesTelemetry[d.id] || d.telemetry || {} }))
        .sort((a, b) => Number(!!b.tel.connected) - Number(!!a.tel.connected));
    return (
        <Sheet title={title} onClose={onClose}>
            {rows.length === 0 && <div className="m-empty">No drones registered. Add one from the DRONES tab.</div>}
            {rows.map(({ d, tel }) => {
                const battery = num(tel.battery_remaining);
                const live = !!tel.connected;
                const selected = d.id === selectedDroneId;
                return (
                    <button
                        key={d.id}
                        type="button"
                        className={`m-b m-list-row${selected ? ' selected' : ''}`}
                        style={{ opacity: live ? 1 : 0.55 }}
                        onClick={() => { onSelect(d); onClose(); }}
                    >
                        <Icon name="helicopter" size={20} style={{ color: selected ? 'var(--m-cyan)' : 'var(--m-on-surface-variant)' }} />
                        <span className="m-list-row-main">
                            <span className="m-list-row-title">{(d.name || d.id).toUpperCase()}</span>
                            <span className="m-list-row-sub">{live ? `MODE ${String(tel.mode || 'N/A').toUpperCase()}` : 'OFFLINE'}</span>
                        </span>
                        {live && (
                            <span className="m-list-row-val" style={{ color: batteryColor(battery) }}>
                                <Icon name="battery_full" size={14} />{battery != null ? `${Math.round(battery)}%` : '—'}
                            </span>
                        )}
                    </button>
                );
            })}
        </Sheet>
    );
}

function Stat({ label, value, color, pulse = false }) {
    return (
        <div className="m-stat">
            <span className="m-stat-label">{label}</span>
            <span className="m-stat-value" style={{ color }}>
                {value}
                {pulse && <span className="m-pulse-dot" />}
            </span>
        </div>
    );
}

/** ALTITUDE / HDG / SPEED / GPS card (Flutter map tab, bottom-left). */
export function TelemetryCard({ telemetry }) {
    const tel = telemetry || {};
    const live = !!tel.connected;
    const alt = num(tel.relative_alt) ?? num(tel.alt);
    const fix = num(tel.gps_fix) ?? 0;
    const sats = num(tel.satellites);
    const dash = (v, f) => (live && v != null ? f(v) : '—');
    return (
        <div className="m-glass m-telemetry-card">
            <Stat label="ALTITUDE" value={dash(alt, (v) => `${v.toFixed(1)}m`)} />
            <Stat label="HDG" value={dash(num(tel.heading), (v) => `${Math.round(v)}°`)} />
            <Stat label="SPEED" value={dash(num(tel.groundspeed), (v) => `${v.toFixed(1)}m/s`)} />
            <Stat
                label="GPS"
                value={!live ? 'NO LINK' : fix >= 3 ? `${sats ?? '—'} SAT` : fix === 2 ? '2D FIX' : 'NO FIX'}
                color={!live || fix < 2 ? 'var(--m-error)' : fix === 2 ? 'var(--m-amber)' : 'var(--m-green)'}
                pulse={live && fix >= 3}
            />
        </div>
    );
}

/** Glass pill of + / − / locate (Flutter zoom group). */
export function ZoomGroup({ onZoomIn, onZoomOut, onLocate }) {
    return (
        <div className="m-glass m-zoom">
            <button type="button" className="m-b m-zoom-btn" onClick={onZoomIn} aria-label="Zoom in"><Icon name="add" /></button>
            <span className="m-zoom-sep" />
            <button type="button" className="m-b m-zoom-btn" onClick={onZoomOut} aria-label="Zoom out"><Icon name="remove" /></button>
            {onLocate && (
                <>
                    <span className="m-zoom-sep" />
                    <button type="button" className="m-b m-zoom-btn" onClick={onLocate} aria-label="Go to drone" style={{ color: 'var(--m-cyan)' }}>
                        <Icon name="near_me" />
                    </button>
                </>
            )}
        </div>
    );
}

/** 2D / 3D chips in a glass pill. */
export function ModeToggle({ value, onChange, options = ['2d', '3d'] }) {
    return (
        <div className="m-glass neon m-mode-toggle">
            {options.map((o) => (
                <button key={o} type="button" className={`m-b m-mode-chip${value === o ? ' active' : ''}`} onClick={() => onChange(o)}>
                    {o.toUpperCase()}
                </button>
            ))}
        </div>
    );
}

/** Round search button that opens a place-search field beside it. */
export function SearchButton({ onSearch }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!q.trim() || busy) return;
        setBusy(true);
        const found = await onSearch(q.trim());
        setBusy(false);
        if (found !== false) { setOpen(false); setQ(''); }
    };
    return (
        <div className="m-search">
            {open && (
                <form className="m-glass neon m-search-field" onSubmit={submit}>
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search a place…"
                        enterKeyHint="search"
                    />
                    <button type="submit" className="m-b m-icon-btn" aria-label="Go" disabled={busy}>
                        <Icon name={busy ? 'progress_activity' : 'arrow_forward'} size={20} />
                    </button>
                </form>
            )}
            <RoundButton icon={open ? 'close' : 'search'} neon title="Search location" onClick={() => setOpen((o) => !o)} color="var(--m-cyan)" />
        </div>
    );
}
